import { useEffect, useRef, useState, type RefObject } from "react";
import { styled } from "~/common/mui";
import { ScrollSlider } from "~/features/search/components/ProductResults/views/NewTabularView/ScrollSlider";

interface ComparisonScrollControllerProps {
  scrollContainerRef: RefObject<HTMLDivElement | null>;
}

export function ComparisonScrollController({ scrollContainerRef }: ComparisonScrollControllerProps) {
  const [sliderValue, setSliderValue] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const isSliderDragging = useRef(false);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    const updateVisibility = () => {
      setIsVisible(container.scrollWidth > container.clientWidth);
    };
    updateVisibility();
    const resizeObserver = new ResizeObserver(updateVisibility);
    resizeObserver.observe(container);
    return () => {
      resizeObserver.disconnect();
    };
  }, [scrollContainerRef]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    const handleScroll = () => {
      if (isSliderDragging.current) {
        return;
      }
      const maxScroll = container.scrollWidth - container.clientWidth;
      if (maxScroll > 0) {
        setSliderValue((container.scrollLeft / maxScroll) * 100);
      }
    };
    container.addEventListener("scroll", handleScroll, {
      passive: true,
    });
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [scrollContainerRef]);

  const handleSliderChange = (_: Event, value: number | number[]) => {
    const sliderValue = Array.isArray(value) ? value[0] : value;
    setSliderValue(sliderValue);
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    const maxScroll = container.scrollWidth - container.clientWidth;
    container.scrollLeft = (sliderValue / 100) * maxScroll;
  };

  if (!isVisible) {
    return null;
  }

  return (
    <ScrollBarContainer>
      <ScrollSlider
        value={sliderValue}
        min={0}
        max={100}
        onChange={handleSliderChange}
        onMouseDown={() => {
          isSliderDragging.current = true;
        }}
        onChangeCommitted={() => {
          isSliderDragging.current = false;
        }}
        sx={{ height: "56px" }}
        scale={undefined}
      />
    </ScrollBarContainer>
  );
}

const ScrollBarContainer = styled("div")(({ theme }) => ({
  height: "40px",
  padding: "0 8px",
  "& .MuiSlider-root": {
    height: "40px",
    padding: "0px",
  },
}));
